import { withRoot } from "@nova/with-root"
import { createSelector, createSlice } from "@reduxjs/toolkit"
import { v4 } from "uuid"
import { deactivateFire } from "features/map/control/slice"
import { GridData } from "./PDFAnnotations/PDFCoordinateNotations"
import { PDFTemplateData, SupportedRegions, ToolName } from "@nova/constants"
import { RootState } from "integrations/redux/rootReducer"
import { formatShortDate } from "@nova/format"
import { PDFJob } from "@nova/protobuf"
import { memoize } from "nodash"
import { PDF_NOVA_LOGO_SRC, pdfLogoAssetUrl } from "plugins/shared/pdfLogos"

export type PDFJobType = {
    portfolioId: string
    projectId: string
    annotations: string[]
    orthos: { path: string; maxZoom: number }[]
    flightpaths: string[]
    hotspots: { path: string; labels: boolean; heatmap: boolean }[]
    polygons: string[]
    icons: string
    layerStyle: string
    kml: string[]
    lineShapes?: string
    pdfRasters: string[]
    polygonShapes?: string
    mapRatio: number
    perimeter?: string
    crs: string
    gridEnabled: boolean
    borderTickEnabled: boolean
    grid: GridData
    notation: string
    tickPrecision: number
    viewport: Record<string, number>
    text: string

    /** @deprecated should not be generated in frontend, but is to bandaid chicken-or-egg scenario for qr code link */
    jobId: string

    /** used as a prefix for downloaded map, defaults to `map` if not provided (map.pdf) */
    filename: string

    page: { size: string; orientation: string }
    colorTheme: "light" | "dark"
    legendPosition: "right" | "bottom"
    // project: {
    //     name: string
    //     details: string
    //     alias: string
    //     date: string
    //     location: string
    // }
}

type PDFExtraDataSlice = {
    snapshot: PDFJobType | null
    deletingById: Record<string, boolean>
}

export const selectPDFJobs = (s: RootState) => s.project.data.PDFs
export const selectPDFJobsMap = createSelector([selectPDFJobs], (pdfs) => {
    return pdfs.reduce(
        (acc, cur) => {
            acc[cur.id] = cur
            return acc
        },
        {} as Record<string, PDFJob>
    )
})
export const selectPDFById = memoize((id: string) =>
    createSelector([selectPDFJobsMap], (map) => {
        return map[id]
    })
)
export const selectSortedPDFJobs = createSelector([selectPDFJobs], (pdfs) =>
    [...pdfs].sort((a, b) => b.createdAt - a.createdAt)
)

const uiStateName = "pdf-extra-data"

const {
    actions: { setSnapshot, setDeleting },
    reducer,
} = createSlice({
    name: uiStateName,
    initialState: { deletingById: {} } as PDFExtraDataSlice,
    reducers: {
        setDeleting: (state, action: { payload: string }) => {
            state.deletingById[action.payload] = !state.deletingById[action.payload]
        },
        setSnapshot: (state, action) => {
            const { snapshot } = action.payload
            state.snapshot = snapshot
        },
    },
})

const { selectSnapshot, selectDeletingPdfs } = withRoot(
    (r: Record<typeof uiStateName, PDFExtraDataSlice>) => r[uiStateName],
    {
        selectSnapshot: () => (s) => s.snapshot,
        selectDeletingPdfs: () => (s) => s.deletingById,
    }
)

const extraDataReducer = { [uiStateName]: reducer } as Record<typeof uiStateName, typeof reducer>

export const pageSizes = [
    '8.5" x 11"',
    '8.3" x 11.7"',
    '11" x 17"',
    '11.7" x 16.5"',
    '24" x 36"',
    '36" x 48"',
]

export enum PageSizes {
    xs,
    sm,
    md,
    lg,
    xl,
    xxl,
}

export const leftLogoDefault = PDF_NOVA_LOGO_SRC
export const leftLogoDark = "/images/logos/nova-logo-with-title.svg"

export const initialState = {
    annotationData: {
        title: "",
        subtitle: "",
        date: formatShortDate({ value: new Date() }),
        perimeterArea: "",
        text1: "All information on this map is provided 'as is' without any warranty or representation whatsoever. This map is intended as reference only. The map contains highly sensitive data. Any other use or widespread reproduction/duplication is strictly prohibited.",
        projection: "",
        datum: "",
        createdAt: "",
        createdBy: "",
        operator: "",
        ratio: "10000",
        displayedRatio: "10000",
        leftLogo: leftLogoDefault,
        rightLogo: "",
    },
    gridData: {
        x: 1,
        y: 1,
        offsetX: 0,
        offsetY: 0,
    },
    borderData: {
        x: 1,
        y: 1,
        offsetX: 0,
        offsetY: 0,
    },
    enabledData: {
        title: true,
        features: true,
        compass: true,
        text1: false,
        text2: false,
        projection: true,
        datum: true,
        createdAt: true,
        createdBy: true,
        operator: false,
        ratio: false,
        scalebar: true,
        inset: false,
        qrcode: true,
        logos: true,
        gridlines: false,
        borderticks: false,
        settings: false,
        hotspotsTable: false,
        useGoogleLinks: false,
    },
    activeMenu: "",
    filename: "",
    jobId: v4(),
    snapshotOpen: false,
    orientation: "Landscape",
    notation: "decimal",
    coordinateSystem: "WGS 84",
    size: PageSizes.xs,
    templateData: {
        mapPosition: {
            enabled: true,
            position: {
                longitude: 0,
                latitude: 0,
            },
        },
        pageSize: true,
        orientation: true,
        notation: true,
        coordinateSystem: true,
    },
    colorTheme: "light",
    legendPosition: "right",
    hideLegend: false,
    disabledItems: {} as Record<string, boolean>,
    orderedItemIndexes: {} as Record<string, number>,
    visibleStatItems: {} as Record<string, boolean>,
    secondaryVisibleStatItems: {} as Record<string, boolean>,
    //update to trigger a rerender for legend renames
    renamedUpdateTimestamp: 0,
    showTextFeatures: false,
}

type PDFSettings = {
    template: PDFTemplateData
    logoUrls: {
        left?: string
        right?: string
    }
}

type PDFDataID = keyof typeof initialState.annotationData
export type PDFButtonID = keyof typeof initialState.enabledData

const {
    actions: {
        setActiveMenu,
        setData,
        toggleData,
        setFilename,
        reset,
        setSnapshotOpen,
        setOrientation,
        setGridData,
        setCoordinateSystem,
        setNotation,
        setBorderData,
        setPageSize,
        setColorTheme,
        setLegendPosition,
        setTemplateMapPosition,
        applyLoadedPDFTemplate,
        toggleDisabledItem,
        setOrderedItemIndexes,
        toggleVisibleStats,
        toggleSecondaryStats,
        setLegendRenameTimestamp,
        setHideLegend,
        toggleShowTextFeatures,
    },
    reducer: dataReducer,
} = createSlice({
    name: "pdf-data",
    initialState: { ...initialState },
    reducers: {
        setActiveMenu: (state, action) => {
            state.activeMenu = action.payload
        },
        setData: (
            state,
            {
                payload,
            }: {
                payload: { type: keyof typeof initialState.annotationData; data: string }
            }
        ) => {
            state.annotationData[payload.type] = payload.data
        },
        toggleData: (state, { payload }: { payload: PDFButtonID }) => {
            state.enabledData[payload] = !state.enabledData[payload]
        },
        setGridData: (state, action) => {
            state.gridData = action.payload
        },
        setBorderData: (state, action) => {
            state.borderData = action.payload
        },
        setFilename: (state, action) => {
            state.filename = action.payload
        },
        setSnapshotOpen: (state, action) => {
            state.snapshotOpen = action.payload
            state.jobId = v4()
        },
        reset: () => {
            return { ...initialState, snapshotOpen: true, jobId: v4() }
        },
        setOrientation: (state, action) => {
            state.orientation = action.payload
        },
        setPageSize: (state, action) => {
            state.size = action.payload
        },
        setColorTheme: (state, action) => {
            state.colorTheme = action.payload
        },
        setLegendPosition: (state, action) => {
            state.legendPosition = action.payload
        },
        setNotation: (state, action: { payload: string }) => {
            state.notation = action.payload
        },
        setCoordinateSystem: (state, action: { payload: string }) => {
            state.coordinateSystem = action.payload
        },
        setHideLegend: (state, action: { payload: boolean }) => {
            state.hideLegend = action.payload
        },
        toggleDisabledItem: (state, action: { payload: string }) => {
            const id = action.payload
            state.disabledItems = { ...state.disabledItems, [id]: !state.disabledItems[id] }
        },
        setOrderedItemIndexes: (state, action: { payload: Record<string, number> }) => {
            state.orderedItemIndexes = action.payload
        },
        setLegendRenameTimestamp: (state, action: { payload: number }) => {
            state.renamedUpdateTimestamp = action.payload
        },
        toggleVisibleStats: (
            state,
            { payload }: { payload: { id: string; defaultValue: boolean } }
        ) => {
            const { id, defaultValue } = payload
            const current = state.visibleStatItems[id]
            const effective = current === undefined ? defaultValue : current
            state.visibleStatItems = { ...state.visibleStatItems, [id]: !effective }
        },
        toggleSecondaryStats: (state, action: { payload: string }) => {
            const id = action.payload
            state.secondaryVisibleStatItems = {
                ...state.secondaryVisibleStatItems,
                [id]: !state.secondaryVisibleStatItems[id],
            }
        },
        setTemplateMapPosition: (
            state,
            action: {
                payload: { enabled?: boolean; position?: { longitude: number; latitude: number } }
            }
        ) => {
            if (action.payload.enabled !== undefined) {
                state.templateData.mapPosition.enabled = action.payload.enabled
            }
            if (action.payload.position !== undefined) {
                state.templateData.mapPosition.position = action.payload.position
            }
        },
        applyLoadedPDFTemplate: (
            state,
            action: {
                payload: {
                    template: PDFTemplateData
                    workspaceId: string
                    region?: SupportedRegions
                }
            }
        ) => {
            const data = action.payload.template
            const workspaceId = action.payload.workspaceId
            const region = action.payload.region

            return {
                // Keep existing values like activeMenu, filename, etc. which are never saved in a template.
                ...state,
                // Apply stored values or use defaults from initialState.
                annotationData: {
                    // Keep existing values like date and ratio which are never saved in a template.
                    ...state.annotationData,
                    // Apply stored values or use defaults from initialState.
                    title: data.titles?.title || initialState.annotationData.title,
                    subtitle: data.titles?.subtitle || initialState.annotationData.subtitle,
                    text1: data.text1 || initialState.annotationData.text1,
                    projection: data.projection || initialState.annotationData.projection,
                    datum: data.datum || initialState.annotationData.datum,
                    createdAt: data.createdAt || initialState.annotationData.createdAt,
                    createdBy: data.createdBy || initialState.annotationData.createdBy,
                    operator: data.operator || initialState.annotationData.operator,
                    displayedRatio: data.ratio || initialState.annotationData.ratio,
                    leftLogo: data.logos?.left
                        ? pdfLogoAssetUrl(workspaceId, data.logos.left.id, region)
                        : initialState.annotationData.leftLogo,
                    rightLogo: data.logos?.right
                        ? pdfLogoAssetUrl(workspaceId, data.logos.right.id, region)
                        : initialState.annotationData.rightLogo,
                },
                gridData: data.gridLines || initialState.gridData,
                borderData: data.borderTicks || initialState.borderData,
                enabledData: {
                    // Keep existing values like features which are never saved in a template.
                    ...state.enabledData,
                    // Apply stored values or use defaults from initialState.
                    title: !!data.titles,
                    compass: !!data.compass,
                    text1: !!data.text1,
                    text2:
                        !!data.projection ||
                        !!data.operator ||
                        !!data.datum ||
                        !!data.createdAt ||
                        !!data.createdBy,
                    projection: !!data.projection,
                    datum: !!data.datum,
                    createdAt: !!data.createdAt,
                    createdBy: !!data.createdBy,
                    operator: !!data.operator,
                    ratio: !!data.ratio,
                    scalebar: !!data.scaleBar,
                    inset: !!data.inset,
                    qrcode: !!data.qrcode,
                    logos: !!data.logos,
                    gridlines: !!data.gridLines,
                    borderticks: !!data.borderTicks,
                    hotspotsTable: !!data.hotspotsTable,
                    useGoogleLinks: !!data.useGoogleLinks,
                },
                orientation: data.orientation || initialState.orientation,
                notation: data.notation || initialState.notation,
                legendPosition: data.legendPosition || initialState.legendPosition,
                colorTheme: data.colorTheme || initialState.colorTheme,
                coordinateSystem: data.coordinateSystem || initialState.coordinateSystem,
                size: data.pageSize || initialState.size,
            }
        },
        toggleShowTextFeatures: (state) => {
            state.showTextFeatures = !state.showTextFeatures
        },
    },
    extraReducers: (builder) => {
        builder.addCase(deactivateFire, () => {
            return { ...initialState }
        })
    },
})

const {
    selectActiveMenu,
    selectData,
    selectEnabled,
    selectJobId,
    selectFilename,
    selectOrientation,
    selectGridData,
    selectNotation,
    selectCoordinateSystem,
    selectBorderData,
    selectPageSize,
    selectColorTheme,
    selectPDFSettings,
    selectPDFData,
    selectLegendPosition,
    selectDisabledItems,
    selectVisibleStatItems,
    selectSecondaryStats,
    selectOrderedItemIndexes,
    selectPDFLegendSize,
    selectLegendRenameTimestamp,
    selectPDFLegendScale,
    selectHideLegend,
    selectShowTextFeatures,
} = withRoot((r: Record<"pdf-data", typeof initialState>) => r["pdf-data"], {
    selectActiveMenu: () => (s) => s.activeMenu,
    selectData: (key: PDFDataID) => (s) => s.annotationData[key],
    selectEnabled: () => (s) => s.enabledData,
    selectJobId: () => (s) => s.jobId,
    selectFilename: () => (s) => s.filename,
    selectOrientation: () => (s) => s.orientation,
    selectGridData: () => (s) => s.gridData,
    selectNotation: () => (s) => s.notation,
    selectCoordinateSystem: () => (s) => s.coordinateSystem,
    selectBorderData: () => (s) => s.borderData,
    selectPageSize: () => (s) => pageSizes[s.size] || pageSizes[PageSizes.sm],
    selectColorTheme: () => (s) => s.colorTheme,
    selectLegendPosition: () => (s) => s.legendPosition,
    selectDisabledItems: () => (s) => s.disabledItems,
    selectOrderedItemIndexes: () => (s) => s.orderedItemIndexes,
    selectVisibleStatItems: () => (s) => s.visibleStatItems,
    selectSecondaryStats: () => (s) => s.secondaryVisibleStatItems,
    selectLegendRenameTimestamp: () => (s) => s.renamedUpdateTimestamp,
    selectHideLegend: () => (s) => s.hideLegend,
    selectPDFLegendSize: () => (s) => {
        let defaultSize = 200
        if (s.legendPosition === "bottom") defaultSize = 150
        else if (s.orientation === "Landscape") defaultSize = 200
        else defaultSize = 150

        if (s.legendPosition === "right") {
            if (s.size === PageSizes.md) defaultSize *= 0.9
            if (s.size === PageSizes.lg) defaultSize *= 0.9
            if (s.size === PageSizes.xl) defaultSize *= 0.6
            if (s.size === PageSizes.xxl) defaultSize *= 0.4
        } else {
            if (s.size === PageSizes.md) defaultSize *= 0.9
            if (s.size === PageSizes.lg) defaultSize *= 0.9
            if (s.size === PageSizes.xl) defaultSize *= 0.7
            if (s.size === PageSizes.xxl) defaultSize *= 0.6
        }

        return defaultSize
    },
    selectPDFLegendScale: () => (s) => {
        if (s.size === PageSizes.xs) return 1
        if (s.size === PageSizes.sm) return 1
        if (s.size === PageSizes.md) return 0.9
        if (s.size === PageSizes.lg) return 0.9
        if (s.size === PageSizes.xl) return 0.6
        if (s.size === PageSizes.xxl) return 0.4
        return 1
    },
    selectPDFSettings: () => (s) => {
        let left: { id: string } | undefined
        let right: { id: string } | undefined

        let leftLogoUrl: string | undefined
        let rightLogoUrl: string | undefined

        if (s.enabledData.logos) {
            if (
                s.annotationData.leftLogo !== "" &&
                s.annotationData.leftLogo !== initialState.annotationData.leftLogo
            ) {
                left = { id: v4() }
                leftLogoUrl = s.annotationData.leftLogo
            }

            if (
                s.annotationData.rightLogo !== "" &&
                s.annotationData.rightLogo !== initialState.annotationData.rightLogo
            ) {
                right = { id: v4() }
                rightLogoUrl = s.annotationData.rightLogo
            }
        }

        // Contains non-redundant PDF template settings, so only the changed and selected preferences
        // are saved to DynamoDB, and disabled or default ones are ignored. Logo blob URLs are separate
        // because they are uploaded to S3 on save rather than stored on the template.
        const pdfSettings: PDFSettings = {
            template: {
                titles: s.enabledData.title
                    ? {
                          title:
                              s.enabledData.title && s.annotationData.title !== ""
                                  ? s.annotationData.title
                                  : undefined,
                          subtitle:
                              s.enabledData.title && s.annotationData.subtitle !== ""
                                  ? s.annotationData.subtitle
                                  : undefined,
                      }
                    : undefined,
                compass: s.enabledData.compass,
                text1: s.enabledData.text1 ? s.annotationData.text1 : undefined,
                projection:
                    s.enabledData.text2 && s.enabledData.projection
                        ? s.annotationData.projection
                        : undefined,
                createdAt:
                    s.enabledData.text2 && s.enabledData.createdAt
                        ? s.annotationData.createdAt
                        : undefined,
                datum:
                    s.enabledData.text2 && s.enabledData.datum ? s.annotationData.datum : undefined,
                createdBy:
                    s.enabledData.text2 && s.enabledData.createdBy
                        ? s.annotationData.createdBy
                        : undefined,
                operator:
                    s.enabledData.text2 && s.enabledData.operator
                        ? s.annotationData.operator
                        : undefined,
                ratio: s.enabledData.ratio ? s.annotationData.displayedRatio : undefined,
                scaleBar: s.enabledData.scalebar || undefined,
                inset: s.enabledData.inset || undefined,
                qrcode: s.enabledData.qrcode || undefined,
                logos:
                    left || right
                        ? {
                              left,
                              right,
                          }
                        : undefined,
                gridLines: s.enabledData.gridlines ? s.gridData : undefined,
                borderTicks: s.enabledData.borderticks ? s.borderData : undefined,
                pageSize: s.templateData.pageSize ? s.size : undefined,
                orientation: s.templateData.orientation ? s.orientation : undefined,
                legendPosition: s.legendPosition,
                colorTheme: s.colorTheme,
                notation: s.templateData.notation ? s.notation : undefined,
                coordinateSystem: s.templateData.coordinateSystem ? s.coordinateSystem : undefined,
                hotspotsTable: s.enabledData.hotspotsTable,
                useGoogleLinks: s.enabledData.useGoogleLinks,
            },
            // These are most likely local blob URLs but could possibly be Nova API URLs if the user
            // creates a template after loading a template that contains previously uploaded logos.
            // They are only used to upload the logos to S3 as part of the template saving process.
            logoUrls: {
                left: leftLogoUrl,
                right: rightLogoUrl,
            },
        }

        return pdfSettings
    },
    selectPDFData: () => (s) => s,
    selectShowTextFeatures: () => (s) => s.showTextFeatures,
})

const selectSnapshotOpen = memoize(() =>
    createSelector(
        // reimplementing selectActiveTool to dodge circular import
        [(state: RootState) => state["toolbox-registry-UI"].active],
        (name) => name === ToolName.PDF
    )
)

export {
    setOrientation,
    selectOrientation,
    setSnapshot,
    selectSnapshot,
    extraDataReducer,
    setDeleting,
    selectDeletingPdfs,
    dataReducer,
    setActiveMenu,
    selectActiveMenu,
    toggleData,
    setData,
    selectData,
    selectEnabled,
    setFilename,
    reset,
    selectJobId,
    setSnapshotOpen,
    selectSnapshotOpen,
    selectFilename,
    selectGridData,
    setGridData,
    setCoordinateSystem,
    setNotation,
    selectCoordinateSystem,
    selectNotation,
    setBorderData,
    selectBorderData,
    setPageSize,
    selectPageSize,
    setColorTheme,
    selectColorTheme,
    setLegendPosition,
    selectLegendPosition,
    setTemplateMapPosition,
    selectPDFSettings,
    selectPDFData,
    applyLoadedPDFTemplate,
    toggleDisabledItem,
    selectDisabledItems,
    selectVisibleStatItems,
    toggleVisibleStats,
    selectSecondaryStats,
    toggleSecondaryStats,
    setOrderedItemIndexes,
    selectOrderedItemIndexes,
    selectPDFLegendSize,
    selectPDFLegendScale,
    setLegendRenameTimestamp,
    selectLegendRenameTimestamp,
    selectHideLegend,
    setHideLegend,
    selectShowTextFeatures,
    toggleShowTextFeatures,
}
